import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { roleLabel } from "@/lib/labels";
import { canAccess } from "@/lib/permissions";
import {
  Gauge, UsersThree, Briefcase, Clock, EnvelopeSimple,
  ListChecks, Shield, UserCircle, SignOut, Buildings, Calculator, ClipboardText, CalendarBlank
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: Gauge, end: true, testid: "nav-dashboard", module: "dashboard" },
  { to: "/clients", label: "Clients", icon: Buildings, testid: "nav-clients", module: "clients" },
  { to: "/missions", label: "Missions", icon: Briefcase, testid: "nav-missions", module: "missions" },
  { to: "/temps", label: "Temps", icon: Clock, testid: "nav-time", module: "time" },
  { to: "/lettres-mission", label: "Lettres de mission", icon: EnvelopeSimple, testid: "nav-letters", module: "letters" },
  { to: "/programmes-travail", label: "Programmes de travail", icon: ListChecks, testid: "nav-programs", module: "programs" },
  { to: "/lcb-ft", label: "LCB-FT", icon: Shield, testid: "nav-lcbft", module: "lcbft" },
  { to: "/controle-qualite", label: "Contrôle qualité", icon: ClipboardText, testid: "nav-qc", module: "qc" },
  { to: "/comptabilite", label: "Comptabilité OHADA", icon: Calculator, testid: "nav-accounting", module: "accounting" },
  { to: "/echeances", label: "Échéances fiscales", icon: CalendarBlank, testid: "nav-echeances", module: "echeances" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV.filter((n) => canAccess(user?.role, n.module));

  return (
    <div className="min-h-screen flex bg-[#F8F8FC] text-[#0A0A0A]">
      {/* Sidebar — charte JN Expertise : bleu royal, actif lavande */}
      <aside className="hidden md:flex w-64 flex-col border-r border-[#2E3192] bg-[#1E2066]">
        <div className="p-5 border-b border-[#2E3192] bg-[#15174B]">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_time-track-expert/artifacts/0nksxe8x_JN%20EXPERTISE_logo_original.png"
              alt="JN Expertise"
              className="h-10 w-10 rounded-md bg-white object-contain p-0.5"
              data-testid="sidebar-logo"
            />
            <div>
              <div className="font-serif-display text-xl leading-none text-white">JN Expertise</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C5B9E4] mt-1">Cabinet • Suite intégrée</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#C5B9E4] text-[#1E2066] font-medium shadow-sm" : "text-slate-200 hover:bg-[#2E3192]/60 hover:text-white"
                }`
              }
            >
              <n.icon size={18} weight="regular" />
              <span>{n.label}</span>
            </NavLink>
          ))}
          {canAccess(user?.role, "users") && (
            <NavLink
              to="/collaborateurs"
              data-testid="nav-collab"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#C5B9E4] text-[#1E2066] font-medium shadow-sm" : "text-slate-200 hover:bg-[#2E3192]/60 hover:text-white"
                }`
              }
            >
              <UsersThree size={18} />
              <span>Collaborateurs</span>
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-[#2E3192]">
          <div className="flex items-center gap-2 text-xs text-[#C5B9E4]">
            <UserCircle size={20} />
            <div className="min-w-0">
              <div className="truncate font-medium text-white" data-testid="current-user-name">{user?.full_name}</div>
              <div className="truncate">{roleLabel(user?.role)} • {user?.location}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="mt-3 w-full flex items-center justify-center gap-2 text-xs px-3 py-2 border border-[#C5B9E4]/40 text-[#E4DEF5] rounded-md hover:bg-[#2E3192]/60 transition-colors"
          >
            <SignOut size={14} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#1E2066]/95 border-b border-[#2E3192] px-4 py-3 flex items-center justify-between">
        <div className="font-serif-display text-xl text-white">JN Expertise</div>
        <button data-testid="logout-btn-mobile" onClick={logout} className="text-xs px-2 py-1 border border-[#C5B9E4]/40 text-[#E4DEF5] rounded">
          <SignOut size={14} />
        </button>
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        {/* Mobile nav pills */}
        <div className="md:hidden flex gap-2 overflow-x-auto p-3 border-b border-[#E2E4F0] bg-white sticky top-14 z-30">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-full text-xs border ${isActive ? "bg-[#2E3192] text-white border-[#2E3192]" : "border-[#C5B9E4]/60 text-[#2E3192]"}`
              }>
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="p-4 sm:p-8 max-w-full overflow-hidden">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
